import React from 'react'
import Head from 'components/Head'
import SiteLayout from 'components/SiteLayout'
import THEME from 'utils/theme'
import { usePageView } from 'hooks'

const TABLE_OF_CONTENTS = [
  { name: 'Introduction', link: 'introduction' },
  { name: 'Content', link: 'content' },
  { name: 'Copyright', link: 'copyright' },
  { name: 'Your Account / Password Security / Suspension', link: 'your-account' },
  { name: 'Acceptable Use', link: 'acceptable-use' },
  { name: 'Orders For Products', link: 'orders-for-products' },
  { name: 'Outside Materials & Merchants', link: 'outside-materials' },
  { name: 'Disclaimers and Limitation of Liability', link: 'disclaimers-liability' },
  { name: 'Legality', link: 'legality' },
  { name: 'Applicable Law; Jurisdiction', link: 'applicable-law' },
  { name: 'Indemnity For Third-Party Claims', link: 'third-party-claims' },
  { name: 'Dispute Resolution', link: 'dispute-resolution' },
  { name: 'Miscellaneous', link: 'miscellaneous' }
]

const TermsOfUse = () => {
  usePageView()
  const updatedAt = 'June 25, 2024'
  const previousVersion = 'April 1, 2022'

  return (
    <SiteLayout background={THEME.COLORS.BACKGROUND_GRAY} hideBanner>
      <Head title='Terms of Use | Barstool Sports' />
      <section className='container'>
        <article className='staticPage'>
          <header className='staticPage__header'>
            <h1>Terms of Use</h1>
          </header>
          <section className='staticPage__content'>
            <p>
              <div>
                <em>Last updated: {updatedAt}</em>
              </div>
              <em>Previous Version: {previousVersion}</em>
              <ul>
                {TABLE_OF_CONTENTS.map(({ link, name }, index) => (
                  <li key={index}>
                    <a href={`#${link}`}>{name}</a>
                  </li>
                ))}
              </ul>
            </p>
            <p>
              <span>
                <em>Important</em>
              </span>
              : These Terms require all disputes between us to go through binding arbitration instead of government
              court. That means you waive any right to have disputes decided (1) by a judge or jury and (2) in class or
              representative actions in court. You can opt-out of arbitration for thirty (30) days after you first agree
              to these Terms. See <a href='#opt-out'>opt-out</a> under{' '}
              <a href='#dispute-resolution'>Dispute Resolution</a> to learn more.
            </p>
            <p>
              <em>
                We include brief summaries before many sections to make reading and understanding this agreement easier.
                The summaries do <span>not</span> replace the text of each section, and you should still read each
                section in its entirety
              </em>
            </p>
            <p>
              <b id='introduction' className='underline'>
                1. INTRODUCTION
              </b>
            </p>
            <p>
              <em>
                This is a contract between you and Barstool. You can contact us if you have any questions or concerns.
                If you do not understand or agree to these Terms, don’t use the Services.
              </em>
            </p>
            <p>
              <b>
                <em>About Rough N' Rowdy</em>
              </b>
            </p>
            <p>
              Barstool Brawl, LLC (“<b>Barstool</b>”, "<b>RnR</b>", “<b>we</b>”, “<b>our</b>” or “<b>us</b>”) operates
              the Rough N’ Rowdy platform, including our websites, social media pages and apps (the foregoing, and all
              content, features, and materials thereon, shall hereinafter be collectively referred to as the “
              <b>Platform</b>”).
            </p>
            <p>
              <b>
                <em>The Agreement</em>
              </b>
            </p>
            <p>
              These Terms of Use and the following policies ("<b>Our Policies</b>”) collectively form the “
              <b>Agreement</b>” that applies when you use the Platform:
              <ul>
                <li>
                  our <a href='https://www.barstoolsports.com/privacy-policy'>Privacy Policy</a>, which explains how we
                  collect and use your and information and data,
                </li>
                <li>
                  our{' '}
                  <a href='https://www.barstoolsports.com/content-policy'>
                    Exclusive Content License & Submission Policy
                  </a>
                  , which governs your submission and our use of your content (“<b>Your Content</b>”),
                </li>
                <li>
                  our <a href='http://www.barstoolsports.com/digital-sale-terms'>Digital Sale Terms</a>, which apply to
                  purchases of subscriptions and one-time digital content;
                </li>
                <li>
                  our <a href='http://www.barstoolsports.com/messaging-terms'>Messaging Terms and Conditions</a>, which
                  apply if you opt-in to receive recurring automated marketing text messages from us;
                </li>
                <li>
                  and any other governing rules or policies you agree to in connection with participation in particular
                  features or services on the Platform (for instance, to enter a contest).
                </li>
              </ul>
              By using the Platform, you agree to be bound by this Agreement. Please contact us at
              support@barstoolsports.com with any questions regarding this Agreement.
            </p>
            <p>
              <b>
                <em>Updates</em>
              </b>
            </p>
            <p>
              We may modify these Terms and Our Policies from time to time, effective upon posting on the Platform or as
              otherwise indicated atop the applicable document. If we make material changes to these Terms, we will try
              to notify you using the contact information in your Account, by posting a temporary notice on the homepage
              of the Platform or taking any other measure the law may require. For clarity, updates to these Terms do
              not create a new or renewed opportunity to opt-out of arbitration.
            </p>
            <p>
              Use of the Platform will always remain subject to the current versions of these Terms and Our Policies. So
              please review these Terms and Our Policies regularly to ensure you remain aware of the current Agreement.
              If you do not agree with anything in the Agreement, stop using the Platform immediately.
            </p>
            <p>
              <b id='content' className='underline'>
                2. CONTENT
              </b>
            </p>
            <p>
              While using the Platform, you will have access to: (i) content that we provide on and through our Services
              (“<b>Our Content</b>”), (ii) content that you upload or provide while using our Services (“
              <b>Your Content</b>”) and (iii) content embedded from third-party sources or uploaded or provided by other
              users and third parties to the Platform (“<b>User Content</b>”). In this agreement, “<b>content</b>”
              includes, without limitation, all text, images, video, audio, or other material on the Services.
            </p>
            <p>
              <b>
                <em>Proprietary Rights</em>
              </b>
            </p>
            <p>
              Except for Your Content and User Content, all materials contained on the Platform, including Our Content,
              and the software, graphics, text and look and feel of the Platform, and all trademarks (including BARSTOOL
              SPORTS®), copyrights, patents and other intellectual property rights related thereto (“
              <b>Proprietary Materials</b>”), are owned or controlled by Barstool., our subsidiaries or affiliated
              companies, contributors, our third party licensors, and/or our advertising partners.
            </p>
            <p>
              You may not modify, remove, delete, augment, add to, publish, transmit, participate in the transfer or
              sale of, create derivative works from, or in any way exploit any Proprietary Materials or any other aspect
              of the Platform (including User Content), in whole or in part, unless specifically stated otherwise.
            </p>
            <p>
              Subject to your compliance with this Agreement, we grant you a non-exclusive, non-transferable, revocable
              limited license, subject to the limitations herein, to access and use the Platform and Proprietary
              Materials for your own non-commercial entertainment purposes, consistent with the intended purpose of the
              Platform. You agree not to use the Platform for any other purpose.
            </p>
            <p>
              <b>
                <em>Validity of Assertions</em>
              </b>
            </p>
            <p>
              Our Platform is intended to provide you with humorous content and contains published rumors, speculations,
              assumptions and opinions in addition to factual information. We often comment on articles published by
              third parties. Therefore, any statements about facts not personally known to our authors should be
              interpreted as merely statements of alleged facts.
            </p>
            <p>
              <b>
                <em>Content Related to Gambling/Betting</em>
              </b>
            </p>
            <p>
              Certain sites within our Platform, including Barstool Bets, may include content related to gambling/sports
              betting. All this content is being provided for entertainment purposes only. It should not be seen as
              advice or the endorsement of any form of gambling. Our Platform does not give you the opportunity to
              gamble or wager anything of value. If you choose to gamble outside of our Platform, you do so at your own
              risk.
            </p>
            <p>
              If you have concerns about your gambling - or if you’re concerned about a friend or family member –
              National Council on Problem Gambling (NCPG) offers support and counseling referrals that may help you
              through personal, gaming-related issues. The NCPG operates a 24-hour confidential toll-free helpline that
              you can reach by phone or text at 1-800-522-4700 or chat at ncpgambling.org/chat. For additional
              information or resources relating to problems with gambling, please visit the NCPG and/or National Center
              for Responsible Gaming.
            </p>
            <p>
              <b>
                <em>Your Content</em>
              </b>
            </p>
            <p>
              All terms relating to the posting, use and distribution of Your Content can be found in our{' '}
              <a href='https://www.barstoolsports.com/content-policy'>Exclusive Content License & Submission Policy</a>.
            </p>
            <p>
              <b>
                <em>Third Party Content & User Content</em>
              </b>
            </p>
            <p>
              You acknowledge that we host User Content and content provided by third parties. Any opinions, advice,
              statements, judgments, services, offers, or other information that constitutes part of the content
              expressed or made available by third parties, including User Content and any content posted by our
              contributors, are those of the respective authors and not of Barstool, its affiliates or any of their
              officers, directors, employees, or agents.
            </p>
            <p>
              Just as you retain complete ownership of Your Content (subject to the license to us in our Exclusive
              Content License & Submission Policy), all other users of the Platform retain ownership of their User
              Content. You may not modify, remove, delete, augment, add to, publish, transmit, participate in the
              transfer or sale of, create derivative works from, or in any way exploit any User Content in whole or in
              part.
            </p>
            <p>
              While we retain the right to filter or reject User Content and other content posted by third parties, we
              review User Content and other third party content in a limited gate-keeper fashion. We have no obligation
              to investigate whether any content or other User Content violates this Agreement and will not do so in
              most cases. We neither endorse nor are responsible for User Content or any other content posted to the
              Platform by anyone other than Barstool.
            </p>
            <p>
              <b id='copyright' className='underline'>
                3. COPYRIGHT
              </b>
            </p>
            <p>
              <em>
                If you think content on the Platform infringes your copyright, email us at{' '}
                <a href='mailto:copyright@barstoolsports.com'>copyright@barstoolsports.com</a> with the information
                described in this section.
              </em>
            </p>
            <p>
              If you believe that any content, including User Content or other materials, posted on the Platform
              infringe your copyrights, we will respond promptly to any properly submitted notice containing the
              information detailed below. Pursuant to Title 17, United States Code, Section 512(c)(2), written
              notifications of claimed copyright infringement should be sent to our Designated Agent at{' '}
              <a href='mailto:copyright@barstoolsports.com'>copyright@barstoolsports.com</a>.
            </p>
            <p>
              To be effective, the notification must be a written communication that includes the following:
              <ul>
                <li>
                  A physical or electronic signature of person authorized to act on behalf of the owner of an exclusive
                  right that is allegedly infringed;
                </li>
                <li>
                  Identification of the copyrighted work claimed to have been infringed, or if multiple copyrighted
                  works at a single online site are covered by a single notification, a representative list of such
                  works at that site;
                </li>
                <li>
                  Identification of the material that is claimed to be infringing or to be the subject of infringing
                  activity and that is to be removed or access to which is to be disabled, and information reasonably
                  sufficient to permit us to locate the material;
                </li>
                <li>
                  Information reasonably sufficient to permit us to contact the complaining party, such as an address,
                  telephone number, and if available, an electronic mail address at which the complaining party may be
                  contacted;
                </li>
                <li>
                  A statement that the complaining party has a good faith belief that use of the material in the manner
                  complained of is not authorized by the copyright owner, its agent, or the law; and
                </li>
                <li>
                  A statement that the information in the notification is accurate, and under penalty of perjury, that
                  the complaining party is authorized to act on behalf of the owner of an exclusive right that is
                  allegedly infringed.
                </li>
              </ul>
            </p>
            <p>
              <b id='your-account' className='underline'>
                4. YOUR ACCOUNT / PASSWORD SECURITY / SUSPENSION, TERMINATION
              </b>
            </p>
            <p>
              To use certain features of the Platform, you will be required to register for an account with us (an “
              <b>Account</b>”). When creating or updating an Account, you are required to provide us with certain
              personal information, such as your e-mail address or, in the case of purchases, your name,
              billing/shipping address and credit card information. Your personal information will be held and used in
              accordance with our Privacy Policy.
            </p>
            <p>
              You may never use another’s Account without permission or permit another to use your Account. You may not
              create more than one Account. You are prohibited from registering a new Account if you have previously had
              an Account terminated.
            </p>
            <p>
              You are responsible for all activity that occurs through your Account, so (i) keep your password
              confidential and (ii) restrict access to your device. Notify us immediately of any breach of security or
              unauthorized use of any part of your Account.
            </p>
            <p>
              Although we will not be liable for your losses caused by any unauthorized use of your Account, whether
              with or without your knowledge, you may be liable for our losses or the losses of our contributors, third
              party licensors, content providers, merchants, advertisers, sponsors, and service providers due to such
              unauthorized use.
            </p>
            <p>
              Without limiting anything in this Agreement, we reserve the right, in our sole discretion, to restrict,
              suspend, or terminate your Account and/or your access to all or any part of the Platform at any time, for
              any or no reason, with or without prior notice, and without liability. We reserve the right to restrict,
              suspend and/or terminate your access to any part of the Platform if we determine, in our sole discretion,
              that you have violated any of the terms of this Agreement.
            </p>
            <p>
              <b id='acceptable-use' className='underline'>
                5. ACCEPTABLE USE
              </b>
            </p>
            <p>
              You, as a user, agree to use the Platform only for lawful purposes and always in compliance with this
              Agreement. Without limiting anything else above, specific prohibited activities include, but are not
              limited to:
              <ol>
                <li>
                  depicting, encouraging or partaking in criminal or tortious activity, including fraud, trafficking in
                  obscene or illegal material, drug dealing or using, underage drinking, gambling, violence, harassment,
                  stalking, spamming, spimming, sending of viruses or other harmful files,
                </li>
                <li>
                  depicting, encouraging or partaking in dangerous activities or conduct that is abusive, threatening,
                  obscene, defamatory or libelous;
                </li>
                <li>
                  posting, uploading or sharing content that violates any of the prohibitions herein or that constitutes
                  copyright infringement, patent infringement, or theft of trade secrets;
                </li>
                <li>
                  attempting to circumvent, disable or otherwise interfere with security-related features of the
                  Platform or features that prevent or restrict use or copying of any content or enforce limitations on
                  use of the Platform or the content therein;
                </li>
                <li>
                  using any software that intercepts, mines, or otherwise collects information about other users or
                  copies and stores any Proprietary Materials (as defined below);
                </li>
                <li>
                  interfering with, disrupting, or creating an undue burden on the Platform or the networks or services
                  connected to the Platform;
                </li>
                <li>attempting to impersonate another user or person;</li>
                <li>soliciting personal information from anyone under 18;</li>
                <li>
                  collecting, harvesting, soliciting or posting passwords or personally identifiable information from
                  other users;
                </li>
                <li>
                  using the account, username, or password of another account holder at any time or disclosing your
                  password to any third party or permitting any third party to access your Account;
                </li>
                <li>
                  using any information obtained from the Platform in order to harass, abuse, or harm another person,
                  whether a user or a contributor;
                </li>
                <li>using the Platform in a commercial manner.</li>
              </ol>
              You represent and warrant that neither your actions on the Platform nor Your Content will violate any of
              the prohibited conduct described above. Any conduct by you in violation of the foregoing prohibitions may
              result in the suspension or termination of your Account and your access to the Platform.
            </p>
            <p>
              <b id='orders-for-products' className='underline'>
                6. ORDERS FOR PRODUCT
              </b>
            </p>
            <p>
              <em>
                You agree to pay us in full when you order products or services. Most products can be returned for
                refund within 14 days. Visit the links in this section for specifics about your product.
              </em>
            </p>
            <p>
              If you order products through our Platform, including the official Barstool Store, Barstool.tv or Old Row,
              the following terms apply:
            </p>
            <p>
              <span>
                <b>(a)</b> Digital Products and Subscriptions
              </span>
            </p>
            <p>
              We may offer digital content on a subscription or one-time-purchase basis. All recurring subscriptions and
              other digital content purchases are governed by our Digital Sale Terms. EU and UK consumers may refund
              digital content purchases within 14 days of purchase, provided the purchased content has not been accessed
              (e.g., until you begin to stream or download a file).
            </p>
            <p>
              <span>
                <b>(b)</b> Merchandise and Physical Goods
              </span>
            </p>
            <p>
              Physical products and goods may have limited quantities and are subject to return or exchange only within
              14 days of purchase, except as otherwise described in our{' '}
              <a href='https://store.barstoolsports.com/pages/returns-exchanges'>Returns & Exchanges</a> page. You can
              find details about ordering, shipping, discount codes and other merchandise questions on the pages beneath
              the “Help” heading in the Barstool Store website footer.
            </p>
            <p>
              We have made every effort to display as accurately as possible the colors and images of our products that
              appear at the store. We cannot guarantee that your computer monitor’s display of any color will be
              accurate.
            </p>
            <p>
              We reserve the right to limit the quantities of any products or services that we offer. All descriptions
              of products are subject to change at any time without notice, at the sole discretion of us. We reserve the
              right to discontinue any product at any time. Any offer for any product made on the Platform is void where
              prohibited.
            </p>
            <p>
              We reserve the right to refuse any order you place with us. In the event that we cancel an order, we may
              attempt to notify you by contacting the e-mail and/or billing address/phone number provided at the time
              the order was made. We reserve the right to limit or prohibit orders that, in our sole judgment, appear to
              be placed by dealers, resellers or distributors.
            </p>
            <p>
              <span>
                <b>(c)</b> Pricing and Taxes
              </span>
            </p>
            <p>
              All pricing is subject to change without prior notice. We reserve the right to adjust the price of any
              product sold through our online stores at any time in our sole discretion. In the event that a product is
              listed on the Website at an incorrect price due to a typographical error or error in pricing information,
              we will have the right to refuse or cancel any orders placed for the product listed at the incorrect
              price. We will have the right to refuse or cancel any such orders whether or not the order has been
              confirmed and your credit card charged. If your credit card has already been charged for the purchase and
              your order is canceled, we will make reasonable attempts to notify you and will issue a credit to your
              credit card account in the amount of the incorrect price.
            </p>
            <p>
              To the extent that we are required by law to charge and collect taxes on products that we sell, such taxes
              are charged based on the tax laws applicable to the location to which the order is being shipped or
              delivered. At checkout, all appropriate taxes will be added to the order total. The tax amount displayed
              during checkout is an estimate of the tax applicable to your order. This amount may vary slightly from the
              actual amount of tax payable in connection with your order due to different tax rates which apply as a
              result of the origin and destination of the item(s) being purchased, as well as other factors.
            </p>
            <p>
              <b id='outside-materials' className='underline'>
                7. OUTSIDE MATERIALS & MERCHANTS
              </b>
            </p>
            <p>
              <span>
                <b>(a)</b> <span>Links, App Stores and other outside materials</span>
              </span>
            </p>
            <p>
              The Platform may link to, embed, integrate or connect third party services (“<b>Outside Materials</b>”).
              Outside Materials may be subject to additional legal terms (“<b>Outside Terms</b>”) made available by
              their third-party provider.
              <ul>
                <li>
                  For example, by downloading or using a Barstool app from the Apple App Store, you are agreeing to
                  Apple’s Licensed Application End User License Agreement (“<b>Apple Terms</b>”). This Agreement governs
                  if there is a conflict with the Apple Terms.
                </li>
              </ul>
              The Agreement does not apply to Outside Materials. Barstool is also not a party to Outside Terms. By using
              the Services, you acknowledge and agree that we are not responsible for, and disclaim all liability for,
              the performance and reliability of Outside Materials and any act or omission of any provider of Outside
              Materials. We do not warrant, endorse or otherwise guarantee the Outside Materials’ integration,
              interoperation or support with the Services.
              <div className='spacer' />
              If you have any complaints in connection with any Outside Materials or Outside Terms, please contact the
              third-party provider directly, your state Attorney General or the Federal Trade Commission at www.ftc.gov.
            </p>
            <p>
              <span>
                <b>(b)</b> <span>Outside Merchants</span>
              </span>
            </p>
            <p>
              Certain parts of the Platform, such as the Barstool Store, may enable you to order and receive products,
              information and services from businesses that are not owned or operated by us (collectively, “
              <b>Outside Merchants</b>”). The Platform may also prompt you to establish an account with an Outside
              Merchant, subject to Outside Terms. The Outside Terms—and any purchase, payment, warranty, guarantee,
              delivery, maintenance, refund and all other matters concerning the merchandise, services or information,
              opinion or advice ordered or received from such businesses—are solely between you and the Outside
              Merchant.
              <div className='spacer' />
              We do not endorse, warrant, or guarantee such products, information, or services, and are not liable for
              the accuracy, completeness, or usefulness of such information or the quality of availability of such
              products or services. We will not be a party to or in any way be responsible for monitoring any
              transaction between you and third party providers of such products, services, or information, or for
              ensuring the confidentiality of your credit card information. Any separate charges or obligations you
              incur in your dealings with these third parties are your responsibility and are not part of the fee, if
              any, charged for the Platform.
            </p>
            <p>
              <b id='disclaimers-liability' className='underline'>
                8. DISCLAIMERS AND LIMITATION OF LIABILITY
              </b>
            </p>
            <p>
              <b>
                <em>Disclaimer of warranties </em>
              </b>
              <div className='spacer' />
              THE PLATFORM AND ALL PRODUCTS AND SERVICES OFFERED HEREON ARE PROVIDED ON AN ‘AS IS’ AND ‘AS AVAILABLE’
              BASIS. TO THE FULLEST EXTENT PERMISSIBLE BY APPLICABLE LAW, WE DISCLAIM ALL IMPLIED WARRANTIES, SUCH AS
              IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
              <div className='spacer' />
              WITHOUT LIMITING THE FOREGOING, NEITHER WE NOR ANY BARSTOOL PROVIDER MAKES ANY REPRESENTATION OR WARRANTY
              OF ANY KIND, EXPRESS OR IMPLIED: (I) AS TO THE OPERATION OF THE PLATFORM, OR THE INFORMATION, CONTENT,
              MATERIALS OR PRODUCTS INCLUDED THEREON; (II) THAT USE OF THE PLATFORM WILL BE 100% SECURE, UNINTERRUPTED
              OR ERROR-FREE; (III) AS TO THE ACCURACY, RELIABILITY, OR CURRENCY OF ANY INFORMATION, CONTENT, OR SERVICE,
              PROVIDED THROUGH THE PLATFORM; OR (IV) THAT THE PLATFORM OR COMMUNICATIONS SENT FROM OR ON BEHALF OF
              BARSTOOL ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
              <div className='spacer' />
              BARSTOOL EXPRESSLY DISCLAIMS ANY AND ALL LIABILITY FOR THE ACTIONS OF ITS USERS OR THE CONTENTS OF ANY
              USER CONTENT.
              <div className='spacer' />
              <b>
                <em>Excluded liabilities</em>
              </b>
              <div className='spacer' />
              NEITHER WE NOR ANY BARSTOOL PROVIDER WILL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL OR CONSEQUENTIAL
              DAMAGES ARISING FROM THE USE OF THE PLATFORM OR THE PURCHASE OF ANY PRODUCT THEREFROM, EVEN IF WE OR SUCH
              BARSTOOL PROVIDER HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              <div className='spacer' />
              <b>
                <em>Maximus damages</em>
              </b>
              <div className='spacer' />
              UNDER NO CIRCUMSTANCES WILL BARSTOOL BE LIABLE TO YOU FOR MORE THAN THE GREATER OF (i) $100 and (ii) THE
              AMOUNT YOU HAVE PAID BARSTOOL IN THE ONE HUNDRED AND EIGHTY (180) DAYS IMMEDIATELY PRECEDING THE DATE YOUR
              CLAIM AROSE.
              <div className='spacer' />
              Certain state laws do not allow limitations on implied warranties or the exclusion or limitation of
              certain damages. These limitations apply only to the extent they are not prohibited by applicable law. To
              the extent permissible, any implied warranties that cannot be excluded are limited to ninety (90) days, or
              such longer period as applicable law requires.
            </p>
            <p>
              <b id='legality' className='underline'>
                9. LEGALITY
              </b>
            </p>
            <p>
              You are subject to all laws of the state(s) and countries in which you reside and from which you access
              the Platform and are solely responsible for obeying those laws. You agree we cannot be held liable if laws
              applicable to you restrict or prohibit your use or activity on the Platform. We make no representations or
              warranties, implicit or explicit, as to your legal right to participate in contests, sweepstakes or
              tournaments offered through the Platform nor shall any person affiliated, or claiming affiliation with the
              Platform have authority to make any such representations or warranties.
            </p>
            <p>
              <b id='applicable-law' className='underline'>
                10. APPLICABLE LAW; JURISDICTION
              </b>
            </p>
            <p>
              The Platform is operated and controlled by us in the State of New York. As such, the laws of the State of
              New York will govern this Agreement, without giving effect to any provisions of New York law that direct
              the choice of another state’s laws.
              <div className='spacer' />
              Subject to the Dispute Resolution section below, you hereby irrevocably and unconditionally consent to
              submit to the exclusive jurisdiction of the courts of the State of New York and of the United States of
              America located in the State of New York for any litigation arising out of or relating to use of or
              purchase made through the Platform (and agree not to commence any litigation relating thereto except in
              such courts), waive any objection to the laying of venue of any such litigation in the New York Courts and
              agree not to plead or claim in any New York Court that such litigation brought therein has been brought in
              an inconvenient forum.
            </p>
            <p>
              <b id='third-party-claims' className='underline'>
                11. INDEMNITY FOR THIRD-PARTY CLAIMS
              </b>
            </p>
            <p>
              You agree to indemnify and hold Barstool, the Barstool Providers, our subsidiaries, and affiliates, and
              our respective officers, agents, partners and employees, harmless from any loss, liability, claim, or
              demand, including reasonable attorneys’ fees, made by any third party due to or arising out of (1) your
              breach of this Agreement and/or any of your representations and warranties set forth herein (including
              those set forth in the Exclusive License & Content Submission Policy) or (2) our distribution,
              exploitation or other use of Your Content.
            </p>
            <p>
              <b id='dispute-resolution' className='underline'>
                12. DISPUTE RESOLUTION{' '}
              </b>
            </p>
            <p>
              <em>
                In the event of a dispute, you and Barstool Sports agree to try to resolve it informally first. If it’s
                not resolved 60 days later, we agree to arbitrate the claim, instead of going to court. You may opt-out
                of arbitration within 30 days after you first accept these Terms.
              </em>
              <div className='spacer' />
              You agree to resolve disputes with Barstool through binding arbitration, except as described in this
              Section 12 (“<b>Arbitration Clause</b>”). The parties expressly waive the right to bring or participate in
              any kind of class, collective, or mass action, private attorney general action, or any other
              representative action. Users of the Platform may opt-out of arbitration under Section 12(j).
              <ol className='lower-alpha'>
                <li>
                  <span>Covered Disputes</span>. You and Barstool agree that any dispute or claim between you and
                  Barstool arising out of or relating to this Agreement or the Services (a “<b>Dispute</b>”) will be
                  resolved by binding arbitration, rather than in court. A Dispute includes any claim or dispute
                  relating to the Services, access and use of the Services, your Account, or any aspects of your
                  relationship or transactions with Barstool. A Dispute also includes any claims or disputes that arose
                  from or involve facts that occurred before the effectiveness of this Agreement and claims that may
                  arise after its termination. For clarity, nothing in this Arbitration Clause prevents either party
                  from settling any Dispute(s) on a class-wide, batch-wide or other multiparty basis.
                </li>
                <li>
                  <span>Exceptions to Arbitration</span>. This Arbitration Clause does not require arbitration of the
                  following types of claims brought by either you or Barstool:{' '}
                  <ol className='lower-roman'>
                    <li>
                      small claims court actions, if the requirements of the court are met and the claims are only on an
                      individual basis; and lamper
                    </li>
                    <li>
                      claims pertaining to intellectual property rights, including trademarks, trade dress, domain
                      names, trade secrets, copyrights and patents.
                    </li>
                  </ol>
                </li>
                <li>
                  <span>Informal Dispute Resolution First</span>. Like you, we want to resolve Disputes without
                  resorting to arbitration. If you have a Dispute with us, before initiating arbitration, you agree to
                  send an individualized request (“<b>Pre-Arbitration Demand</b>”) to{' '}
                  <a href='mailto:dispute@barstoolsports.com'>dispute@barstoolsports.com</a> so that we can work
                  together to resolve the Dispute.
                  <ol className='lower-roman'>
                    <li>
                      This Section 12(c) is a condition precedent to commencing arbitration. The arbitrator will dismiss
                      any arbitration filed without fully and completely complying with these informal dispute
                      resolution procedures.
                    </li>
                    <li>
                      A Pre-Arbitration Demand is only valid when it pertains to, and is on behalf of, a single
                      individual. A Pre-Arbitration Demand brought on behalf of multiple individuals is invalid as to
                      all.
                    </li>
                    <li>
                      The Pre-Arbitration Demand must include: (i) your name, telephone number, mailing address, and
                      email address associated with your account; (ii) the name, telephone number, mailing address and
                      email address of your counsel, if any; (iii) a description of your Dispute; and (iv) your
                      signature.
                    </li>
                    <li>
                      Likewise, if Barstool has a Dispute with you, Barstool will send an email with its individualized
                      Pre-Arbitration Demand, including the requirements listed above, to the email address associated
                      with your Account.
                    </li>
                    <li>
                      If the Dispute is not resolved within sixty (60) calendar days of when either you or Barstool
                      submitted a Pre-Arbitration Demand, an arbitration can be brought.
                    </li>
                    <li>
                      This Section 12(b) does not apply to claims brought under the Exceptions to Arbitration in Section
                      12(a).
                    </li>
                  </ol>
                </li>
                <li>
                  <span>Arbitration Procedure</span>. If, after completing the informal process in Section 12(c), either
                  you or Barstool wishes to initiate arbitration, the initiating party must serve the other party with a
                  demand for arbitration. Any demand for arbitration by you will be sent to the Barstool address in
                  Section 12(e). Barstool will send any arbitration demand to the email address associated with your
                  Account or to your counsel, if any. You and Barstool agree that the Federal Arbitration Act (“
                  <b>FAA</b>”) governs this Arbitration Clause. If the FAA cannot apply, then the state laws governing
                  arbitration procedures where you reside apply.{' '}
                  <ol className='lower-roman'>
                    <li>
                      The arbitration will be administered by National Arbitration and Mediation (“<b>NAM</b>”) under
                      its operative Comprehensive Dispute Resolution Rules and Procedures, available at{' '}
                      <a href='https://www.namadr.com/resources/rules-fees-forms'>
                        https://www.namadr.com/resources/rules-fees-forms
                      </a>
                      . This Arbitration Clause will govern to the extent it conflicts with the arbitration provider’s
                      rules.
                    </li>
                    <li>
                      If the applicable arbitration provider is not available to arbitrate, the parties will select an
                      alternative arbitration provider. If the parties cannot agree on an appropriate alternative
                      arbitration provider, the parties will ask a court of competent jurisdiction to appoint an
                      arbitrator pursuant to 9 U.S.C. § 5. To the extent there is a dispute over which arbitration
                      provider has jurisdiction, a NAM arbitrator will be appointed to resolve that dispute.
                    </li>
                    <li>
                      Arbitration hearings will take place through videoconferencing, unless you and Barstool agree upon
                      another location in writing. A single arbitrator will be appointed.
                    </li>
                    <li>
                      The arbitrator may award damages, declaratory or injunctive relief, and recoverable costs. Any
                      arbitration award may be enforced (such as through a judgment) in any court with jurisdiction over
                      the dispute. An arbitration award will have no preclusive effect in another arbitration or court
                      proceeding involving Barstool and a different individual. The arbitrator will have the exclusive
                      authority to resolve all threshold arbitrability issues, including whether this Arbitration Clause
                      is applicable, unconscionable, or enforceable, as well as any defenses to arbitration. However, a
                      court has exclusive authority to rule on the Class Action Waiver in Section 12(f), including any
                      claim that the section is unenforceable, illegal, void or voidable, or that it has been breached.
                    </li>
                    <li>
                      If a request to proceed in small claims court (see Section 12(b)), is made after an arbitration
                      has been initiated, but before an arbitrator has been appointed, such arbitration will be
                      administratively closed. Any controversy over the small claims court’s jurisdiction will be
                      determined by the small claims court.
                    </li>
                  </ol>
                </li>
                <li>
                  <span>Jury Trial Waiver</span>. You and Barstool agree to waive any constitutional and statutory
                  rights to sue in court and have a trial in front of a judge or a jury. You and Barstool are instead
                  electing that all Disputes will be resolved by arbitration under this Arbitration Clause, except as
                  specified in Section 12(b) above. Court review of an arbitration award is subject to very limited
                  review. Discovery may be limited in arbitration, and procedures are more streamlined than in court.
                </li>
                <li>
                  <span>Class Action Waiver</span>. You and Barstool agree that, except as specified in Section
                  12(f)(iii) below, each of us may bring claims against the other only on an individual basis and not on
                  a class, collective, representative, or mass action basis.{' '}
                  <ol className='lower-roman'>
                    <li>
                      The parties agree to waive all rights to have any Dispute be brought, heard, administered,
                      resolved, or arbitrated on a class, collective, representative, or mass action basis.
                    </li>
                    <li>
                      Subject to this Arbitration Clause, the arbitrator may award declaratory or injunctive relief only
                      in favor of the individual party seeking relief and only to the extent necessary to provide relief
                      to the party’s individual claim.
                    </li>
                    <li>
                      Notwithstanding anything to the contrary in this Arbitration Clause, if a court decides, in a
                      final nonappealable decision, that the limitations of this Section 12(f) are invalid or
                      unenforceable as to a particular claim or request for relief (such as a request for public
                      injunctive relief), you and Barstool agree that that particular claim or request for relief (and
                      only that particular claim or request for relief) will be severed from the arbitration and will be
                      pursued in the courts specified in Section 12.
                    </li>
                  </ol>
                </li>
                <li>
                  <span>Batch Proceedings</span>. To increase the efficiency of administration and resolution of
                  arbitrations, you and Barstool agree that if 25 or more similar arbitration demands (those asserting
                  the same or substantially similar facts or claims, and seeking the same or substantially similar
                  relief), presented by or with the assistance or coordination of the same law firm(s) or
                  organization(s), are filed within a one hundred and eighty (180) day period (“<b>Mass Filing</b>”),
                  the parties agree:{' '}
                  <ol className='lower-roman'>
                    <li>
                      to administer the Mass Filing in batches of 25 demands per batch (or less, if fewer than 25
                      remain) (“<b>Batch Proceedings</b>”) with only one batch filed, processed, and adjudicated at a
                      time;
                    </li>
                    <li>to designate one arbitrator for each batch;</li>
                    <li>
                      to accept applicable fees, including any related fee reduction determined by NAM in its
                      discretion;
                    </li>
                    <li>
                      that no other demands for arbitration that are part of the Mass Filing may be filed, processed, or
                      adjudicated until the prior batch of 25 is filed, processed, and adjudicated;
                    </li>
                    <li>
                      that fees associated with a demand for arbitration included in a Mass Filing, including fees owed
                      by Barstool and the claimants, will only be due after your demand for arbitration is included in a
                      set of Batch Proceedings and that batch is properly designated for filing, processing, and
                      adjudication and
                    </li>
                    <li>
                      that the staged process of batched proceedings, with each set including 25 demands, will continue
                      until each demand (including your demand) is adjudicated or otherwise resolved.
                    </li>
                    <li>
                      Any statutes of limitation, and the requirement to file within eighteen (18) months in Section
                      12(j) below, will remain tolled while any arbitration demands are held in abeyance. While the
                      Batch Proceedings are adjudicated, no other demand for arbitration that is part of the Mass Filing
                      may be processed, administrated, or adjudicated, and no filing or other administrative costs for
                      such a demand for arbitration will be due from either party to the arbitration provider.{' '}
                      <ol className='italic-alpha'>
                        <li>
                          If, contrary to this provision, a party prematurely files an arbitration demand, the parties
                          agree that the arbitration provider must hold those demands in abeyance.
                        </li>
                      </ol>
                    </li>
                    <li>
                      All parties agree that arbitration demands are of a “<b>substantially similar nature</b>” if they
                      arise out of or relate to the same event or factual scenario, raise the same or similar legal
                      issues and seek the same or similar relief. Any party may request that the arbitration provider
                      appoint a sole standing administrative arbitrator (“<b>Administrative Arbitrator</b>”) to
                      determine threshold questions such as (1) whether the Batch Proceeding process is applicable or
                      enforceable, (2) whether particular demand(s) are part of a Mass Filing, and (3) whether demands
                      within a Mass Filing were filed in accordance with this Arbitration Clause.{' '}
                      <ol className='italic-alpha'>
                        <li>
                          To expedite resolution of any such dispute by the Administrative Arbitrator, the parties agree
                          that the Administrative Arbitrator may provide and use any procedures necessary to resolve the
                          dispute promptly. Barstool will pay the Administrative Arbitrator’s costs.
                        </li>
                      </ol>
                    </li>
                    <li>
                      The parties will work in good faith with the arbitrator to complete each Batch Proceeding within
                      one hundred and twenty (120) calendar days of its initial pre-hearing conference. The parties
                      agree that the Batch Proceeding process is designed to achieve an overall faster, more efficient,
                      and less costly mechanism for resolving Mass Filings.
                    </li>
                    <li>
                      This <span>Batch Proceedings</span> provision will in no way be interpreted as authorizing class
                      arbitration of any kind or increasing the number of claims necessary to trigger the applicability
                      of NAM’s Mass Filing Supplemental Dispute Resolution Rules and Procedures. Unless Barstool
                      otherwise consents in writing, Barstool does not agree or consent to class arbitration, private
                      attorney general arbitration, or arbitration involving joint or consolidated claims under any
                      circumstances, except as set forth in this Section 12(g).
                    </li>
                  </ol>
                </li>
                <li>
                  <span>Settlement</span>. At least ten (10) calendar days before the date set for the arbitration
                  hearing, you or Barstool may serve a written offer of judgment upon the other party to allow judgment
                  on specified terms. If the offer is accepted, the offer with proof of acceptance will be submitted to
                  the arbitration provider, who will enter judgment accordingly. If the offer is not accepted before the
                  earlier of the arbitration hearing or thirty (30) calendar days after it is made, it will be deemed
                  withdrawn, and cannot serve as evidence in the arbitration. If an offer made by one party is not
                  accepted by the other party, and the other party fails to obtain a more favorable award, the other
                  party will not recover their post-offer costs and will pay the offering party’s costs from the time of
                  the offer.
                  <div className='spacer' />
                  <div style={{ paddingLeft: '32px', textIndent: '64px' }}>
                    The parties agree that any disputes with respect to settlement offer(s) or offer(s) of judgment in a
                    Mass Filing are to be resolved by a single arbitrator to the extent such offers contain the same
                    material terms. For arbitrations involving represented parties, the represented parties’ attorneys
                    agree to communicate individual offer(s) of judgment to each and every arbitration claimant or
                    respondent to whom such offers are extended.
                  </div>
                </li>
                <li>
                  <span>Arbitration Costs</span>. Except as provided for in a Mass Filing under Section 12(g), your
                  responsibility to pay any filing, administrative, and arbitrator costs will be solely as set forth in
                  the applicable arbitration provider’s rules.
                </li>
                <li>
                  <span>18-Month Filing Deadline</span>. To the extent permitted by applicable law, and notwithstanding
                  any other statute of limitations, any claim or cause of action under this Arbitration Clause (with the
                  exception of disputes under Section 12(b)) must be filed within eighteen (18) months after such claim
                  or cause of action arose. Otherwise, that claim or cause of action will be permanently barred. The
                  statute of limitations and any arbitration cost deadlines remain tolled during the required informal
                  process under Section 12(c) above.
                </li>
                <li id='opt-out'>
                  <span>Opt-Out</span>. You may reject this Arbitration Clause and opt out of arbitration by sending an
                  email to <a href='mailto:opt-out@barstoolsports.com'>opt-out@barstoolsports.com</a> within thirty (30)
                  calendar days of first accepting these Terms. If you have an Account, your opt-out notice must be sent
                  from the email address associated with your Account. No one may opt-out another person. Your notice to
                  opt-out must include your first and last name, address, the email address associated with your Account
                  (if you have an Account), and a clear statement that you decline this Arbitration Clause.
                </li>
                <li>
                  <span>Severability</span>. Except as provided in Section 12(f)(iii) above, if any provision of this
                  Arbitration Clause is found to be illegal or unenforceable, then that provision will be severed. The
                  remaining provisions will still apply and will be interpreted to achieve the closest possible intent
                  to the original intent of this section, inclusive of the severed provision.
                </li>
              </ol>
            </p>
            <p>
              <b id='miscellaneous' className='underline'>
                13. MISCELLANEOUS
              </b>
            </p>
            <p>
              <b>Severability</b>
              <div className='spacer' />
              The provisions of this Agreement are intended to be severable. If for any reason any provision of this
              Agreement shall be held invalid or unenforceable in whole or in part in any jurisdiction, such provision
              shall, as to such jurisdiction, be severed to the extent of such invalidity or unenforceability without in
              any manner affecting the validity or enforceability thereof in any other jurisdiction or the remaining
              provisions hereof in any jurisdiction. The remaining provisions will still apply and will be interpreted
              to achieve the closest possible intent to the original intent of the Agreement, inclusive of the severed
              provision.
              <div className='spacer' />
              <b>Electronic Communications</b>
              <div className='spacer' />
              When you use the Platform or send emails to us, you are communicating with us electronically. You consent
              to receive communications from us electronically. We will communicate with you by email or by posting
              notices through the Platform. You agree that all agreements, notices, disclosures and other communications
              that we provide you electronically satisfy any legal requirement that such communications be in writing.
              <div className='spacer' />
              <b>Waiver; Interpretation</b>
              <div className='spacer' />
              Our failure to exercise or enforce any right or provision of this Agreement shall not operate as a waiver
              of such right or provision. The section titles in this Agreement are for convenience only and have no
              legal or contractual effect. Any obligation in this Agreement on a party not to do something includes an
              obligation not to agree or allow that thing to be done. Any words following the terms “<b>including,</b>”
              “<b>include,</b>” “<b>in particular,</b>” “<b>for example</b>”, “<b>such as</b>” or any similar expression
              are illustrative, non-exhaustive and do not limit the sense of the words, description, definition, phrase
              or term preceding those terms. A reference to writing or written includes e-mail.
              <div className='spacer' />
              <b>Support</b>
              <div className='spacer' />
              Subject to the other provisions of this Agreement, we will attempt to help you with any queries or
              problems that you may have with the Platform or any of your purchases through the Platform.
              <div className='spacer' />
              To reach our customer support team, please e-mail us at{' '}
              <a href='mailto:support@barstoolsports.com'>support@barstoolsports.com</a>. It will expedite your request
              for assistance by providing our representatives with all the information they need to solve your problem
              as quickly as possible.
            </p>
          </section>
        </article>
      </section>
      <style jsx>{`
        .underline,
        span {
          text-decoration: underline;
        }
        .no-underline {
          text-decoration: none;
        }
        .staticPage__content li {
          margin-bottom: 1rem;
        }
        .staticPage__content li:last-of-type {
          margin-bottom: 0;
        }
        .spacer {
          margin-bottom: 1.5rem;
        }
        .lower-roman {
          list-style-type: lower-roman !important;
        }
        .lower-alpha {
          list-style-type: lower-alpha !important;
        }
        .italic-alpha {
          list-style-type: '𝐴. ' !important;
        }
      `}</style>
    </SiteLayout>
  )
}

export default TermsOfUse
